import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {ArrowRightLeft, CirclePlus, Gift, RotateCcw, Trash2, X} from "lucide-react";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {
    fillUserBonus,
    fillUserPlus,
    fillUserPoints,
    manualUserPlusTopup,
    transferUserMoney,
    zeroUserAccount,
} from "./api";
import {mapUserApiError} from "./errors";
import "../../shared/finance/financeOperations.css";
import {useTranslation, i18n} from "../../shared/i18n";


let idempotencySequence = 0;

const newIdempotencyKey = () => {
    if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
        return window.crypto.randomUUID();
    }
    idempotencySequence += 1;
    return `admin-user-command-${Date.now()}-${idempotencySequence}`;
};

const COMMANDS = {
    plus: {
        get label() { return i18n.t("sections.users.p102"); },
        get confirmLabel() { return i18n.t("sections.users.p103"); },
        amountMode: "whole",
        icon: CirclePlus,
        superadminOnly: true,
    },
    points: {
        get label() { return i18n.t("sections.users.p104"); },
        get confirmLabel() { return i18n.t("sections.users.p103"); },
        amountMode: "whole",
        icon: CirclePlus,
        superadminOnly: true,
    },
    bonus: {
        get label() { return i18n.t("sections.users.p105"); },
        get confirmLabel() { return i18n.t("sections.users.p103"); },
        amountMode: "whole",
        icon: Gift,
        countryScoped: true,
    },
    manualTopup: {
        get label() { return i18n.t("sections.users.p106"); },
        get confirmLabel() { return i18n.t("sections.users.p107"); },
        amountMode: "decimal",
        icon: CirclePlus,
        countryScoped: true,
    },
    transfer: {
        get label() { return i18n.t("sections.users.p108"); },
        get confirmLabel() { return i18n.t("sections.users.p109"); },
        amountMode: "whole",
        icon: ArrowRightLeft,
        superadminOnly: true,
    },
    zero: {
        get label() { return i18n.t("sections.users.p110"); },
        get confirmLabel() { return i18n.t("sections.users.p111"); },
        icon: Trash2,
        superadminOnly: true,
        destructive: true,
    },
};

const isPositiveWhole = (value) => /^[1-9]\d*$/.test(value);
const isPositiveDecimal = (value) => {
    if (!/^\d+(?:\.\d{1,2})?$/.test(value)) return false;
    return !/^0+(?:\.0+)?$/.test(value);
};

const focusableElements = (container) => Array.from(container.querySelectorAll(
    "button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])",
));

const responseMessage = (response) => {
    if (typeof response?.message === "string") return response.message;
    if (typeof response?.success === "string") return response.success;
    return i18n.t("sections.users.p112");
};

export const UserWalletCommands = ({
    userId,
    user,
    role,
    canAdjust,
    countryScopedActions = true,
    countryId,
    onUserUpdated,
}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const dialogRef = useRef(null);
    const firstFieldRef = useRef(null);
    const operationTokenRef = useRef(0);
    const pendingRef = useRef(false);
    const activeUserIdRef = useRef(String(userId));
    const [dialog, setDialog] = useState(null);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    const [attempted, setAttempted] = useState(null);
    const [result, setResult] = useState("");

    activeUserIdRef.current = String(userId);

    useEffect(() => {
        operationTokenRef.current += 1;
        pendingRef.current = false;
        setDialog(null);
        setPending(false);
        setError(null);
        setAttempted(null);
        setResult("");
    }, [userId]);

    useEffect(() => {
        if (!dialog) return undefined;
        const frame = window.requestAnimationFrame(() => firstFieldRef.current?.focus());
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (!pendingRef.current) setDialog(null);
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            window.cancelAnimationFrame(frame);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [dialog]);

    if (!canAdjust) return null;

    // Под скоупом «Все страны» бэкенд отдаёт 403 по bonus и manual topup
     // безусловно — не показываем кнопку, которую нельзя нажать.
    const allowedCommands = Object.entries(COMMANDS).filter(([, command]) =>
        (!command.superadminOnly || role === ADMIN_ROLES.SUPERADMIN)
        && (!command.countryScoped || countryScopedActions),
    );

    const openCommand = (type) => {
        const commandUserId = String(userId);
        setDialog({
            type,
            userId: commandUserId,
            idempotencyKey: newIdempotencyKey(),
            values: {amount: "", direction: "Plus", confirmation: ""},
        });
        setPending(false);
        pendingRef.current = false;
        setError(null);
        setAttempted(null);
        setResult("");
    };

    const closeDialog = () => {
        if (pendingRef.current) return;
        setDialog(null);
        setError(null);
        setAttempted(null);
    };

    const requestFor = (snapshot) => {
        const common = {
            userId: snapshot.userId,
            amount: snapshot.values.amount,
            idempotencyKey: snapshot.idempotencyKey,
        };
        if (snapshot.type === "plus") return fillUserPlus(common);
        if (snapshot.type === "points") return fillUserPoints(common);
        if (snapshot.type === "bonus") return fillUserBonus({...common, countryId});
        if (snapshot.type === "manualTopup") return manualUserPlusTopup({...common, countryId});
        if (snapshot.type === "transfer") {
            return transferUserMoney({...common, direction: snapshot.values.direction});
        }
        return zeroUserAccount({userId: snapshot.userId, idempotencyKey: snapshot.idempotencyKey});
    };

    const execute = async (snapshot) => {
        if (pendingRef.current) return;
        const operationToken = ++operationTokenRef.current;
        pendingRef.current = true;
        setPending(true);
        setError(null);
        try {
            const response = await requestFor(snapshot);
            if (
                operationTokenRef.current !== operationToken
                || activeUserIdRef.current !== snapshot.userId
            ) return;
            if (response?.user_data) onUserUpdated(response.user_data);
            setResult(responseMessage(response));
            setDialog(null);
            setAttempted(null);
        } catch (requestError) {
            if (
                operationTokenRef.current !== operationToken
                || activeUserIdRef.current !== snapshot.userId
            ) return;
            const mapped = mapUserApiError(requestError);
            setError(mapped);
            setAttempted(mapped.kind === "validation" ? null : snapshot);
        } finally {
            if (operationTokenRef.current === operationToken) {
                pendingRef.current = false;
                setPending(false);
            }
        }
    };

    const submit = (event) => {
        event.preventDefault();
        if (!dialog || pendingRef.current) return;
        const command = COMMANDS[dialog.type];
        const amount = dialog.values.amount.trim();
        if (command.amountMode === "whole" && !isPositiveWhole(amount)) {
            setError({kind: "validation", message: t("sections.users.p113")});
            return;
        }
        if (command.amountMode === "decimal" && !isPositiveDecimal(amount)) {
            setError({kind: "validation", message: t("sections.users.p114")});
            return;
        }
        if (dialog.type === "zero" && dialog.values.confirmation !== t("sections.users.p115", {v0: dialog.userId})) return;
        execute({...dialog, values: {...dialog.values, amount}});
    };

    const dialogCommand = dialog ? COMMANDS[dialog.type] : null;
    const confirmationText = dialog?.type === "zero" ? t("sections.users.p115", {v0: dialog.userId}) : null;
    const zeroConfirmed = !confirmationText || dialog?.values.confirmation === confirmationText;
    const entity = user?.username || user?.phone_number || t("sections.users.p14", {v0: userId});

    return (
        <section className="admin-card" aria-labelledby="wallet-commands-title">
            <p className="admin-card-title" id="wallet-commands-title">{t("sections.users.p116")}</p>
            {result ? <div role="status" className="admin-card-note">{result}</div> : null}
            <div className="admin-inline-actions">
                {allowedCommands.map(([type, command]) => {
                    const Icon = command.icon;
                    return (
                        <button key={type} type="button" className="admin-button admin-button-secondary" onClick={() => openCommand(type)}>
                            <Icon aria-hidden="true" size={15} /> {command.label}
                        </button>
                    );
                })}
            </div>

            {dialog && dialogCommand ? createPortal(
                <div className="finance-dialog-backdrop">
                    <section
                        ref={dialogRef}
                        className="finance-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-busy={pending}
                        aria-labelledby={titleId}
                    >
                        <form onSubmit={submit}>
                            <header className="finance-dialog-header">
                                <div>
                                    <p className="finance-dialog-entity">{entity}</p>
                                    <h2 id={titleId}>{dialogCommand.label}</h2>
                                </div>
                                <button type="button" className="finance-icon-button" aria-label={t("sections.users.p5")} disabled={pending} onClick={closeDialog}>
                                    <X aria-hidden="true" size={18} />
                                </button>
                            </header>

                            {dialog.type === "transfer" ? (
                                <div className="finance-dialog-fields">
                                    <label>
                                        <span>{t("sections.users.p117")}</span>
                                        <select
                                            ref={firstFieldRef}
                                            value={dialog.values.direction}
                                            disabled={pending || Boolean(attempted)}
                                            onChange={(event) => setDialog((current) => ({
                                                ...current,
                                                values: {...current.values, direction: event.target.value},
                                            }))}
                                        >
                                            <option value="Plus">Extra → Plus</option>
                                            <option value="Points">Plus → Extra</option>
                                        </select>
                                    </label>
                                </div>
                            ) : null}

                            {dialogCommand.amountMode ? (
                                <div className="finance-dialog-fields">
                                    <label>
                                        <span>{t("sections.users.p46")}</span>
                                        <input
                                            ref={dialog.type === "transfer" ? undefined : firstFieldRef}
                                            type="text"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            value={dialog.values.amount}
                                            disabled={pending || Boolean(attempted)}
                                            onChange={(event) => {
                                                setDialog((current) => ({
                                                    ...current,
                                                    values: {...current.values, amount: event.target.value},
                                                }));
                                                setError(null);
                                            }}
                                        />
                                    </label>
                                </div>
                            ) : null}

                            {confirmationText ? (
                                <label className="finance-confirmation-field">
                                    <span>{t("sections.users.p118")} {confirmationText} {t("sections.users.p119")}</span>
                                    <input
                                        ref={firstFieldRef}
                                        value={dialog.values.confirmation}
                                        disabled={pending || Boolean(attempted)}
                                        onChange={(event) => setDialog((current) => ({
                                            ...current,
                                            values: {...current.values, confirmation: event.target.value},
                                        }))}
                                    />
                                </label>
                            ) : null}

                            {error?.message ? <div className="finance-dialog-error" role="alert">{error.message}</div> : null}
                            <footer className="finance-dialog-footer">
                                <button type="button" className="admin-button admin-button-secondary" disabled={pending} onClick={closeDialog}>
                                    {t("sections.users.p11")}
                                </button>
                                {attempted ? (
                                    <button type="button" className="admin-button admin-button-secondary" disabled={pending} onClick={() => execute(attempted)}>
                                        <RotateCcw aria-hidden="true" size={15} /> {t("sections.users.p120")}
                                    </button>
                                ) : null}
                                <button
                                    type="submit"
                                    className={`admin-button ${dialogCommand.destructive ? "finance-confirmation-button-destructive" : "admin-button-primary"}`}
                                    disabled={pending || Boolean(attempted) || !zeroConfirmed}
                                >
                                    {pending ? t("sections.users.p121") : dialogCommand.confirmLabel}
                                </button>
                            </footer>
                        </form>
                    </section>
                </div>,
                document.body,
            ) : null}
        </section>
    );
};
